import React, { useCallback, useEffect, useState } from 'react';
import { Box, Button, Container, Stack, Typography } from '@mui/material';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import { paths } from 'src/routes/paths';
import Iconify from 'src/components/iconify';
import { useAuthContext } from 'src/auth/hooks';
import { fetchInterviewResult } from 'src/services/interview.service';
import { getOrganizationId } from 'src/services/user.service';
import { setError, setLoading, setSuccess } from 'src/store/modalSlice';
import { useSettingsContext } from 'src/components/settings';
import { RouterLink } from 'src/routes/components';
import InterviewResult from '../interview-result';
import CandidateCaptures from '../interview-result/candidate-captures';
import ScreenRecordings from '../interview-result/screen-recordings';
import { useCreateSearchParams } from 'src/hooks/use-create-search-params';
import { useQuery } from 'src/hooks/use-query';
import { useNavigate } from 'react-router';

const InterviewResultDashboard = ({ jobId, interviewId, setTitle }) => {
  // context
  const { user } = useAuthContext();
  const settings = useSettingsContext();
  const dispatch = useDispatch();

  // states
  const [score, setScore] = useState('NA');
  const [totalScore, setTotalScore] = useState(0);
  const [interview, setInterview] = useState(null);
  const [candidateCaptures, setCandidateCaptures] = useState([]);

  // variables
  const organization_id = getOrganizationId(user);
  const navigate = useNavigate();
  const searchQuery = useQuery();
  const tab = searchQuery.get('tab');
  const tablePage = searchQuery.get('tablePage');
  const searchParams = useCreateSearchParams({
    tab,
    tablePage,
  });

  // functions
  const handleBackButton = useCallback(() => {
    if (tab || tablePage) {
      navigate(`${paths.dashboard.jobs.details(jobId)}?${searchParams}`);
    } else {
      navigate(-1);
    }
  }, [jobId, navigate, searchParams, tab, tablePage]);

  // effects
  useEffect(() => {
    dispatch(setLoading(true));

    fetchInterviewResult(jobId, interviewId)
      .then(data => {
        console.log(data);
        setTitle(data.body.name);
        let totaldum = 0;
        data.body.solutions.forEach(sol => {
          totaldum += sol.score;
        });
        setTotalScore(
          parseInt((totaldum / (data.body.solutions.length * 10)) * 100, 10),
        );
        dispatch(setLoading(false));
        if (data.success === true) {
          setInterview(data.body);
          dispatch(setSuccess(data.message));
          if (!data.body.total_score) {
            let tmpScore = 0;
            data.body.solutions.forEach(s => {
              tmpScore = +s.score;
            });
            setScore(parseInt(tmpScore, 10));
          } else {
            setScore(parseInt(data.body.total_score, 10));
          }
        } else {
          dispatch(setError(data.message));
        }
      })
      .catch(err => {
        dispatch(setLoading(false));
        dispatch(setError(err.message));
      });
  }, [organization_id, dispatch, interviewId, jobId, setTitle]);

  useEffect(() => {
    if (interview) {
      interview.solutions.forEach(
        solution =>
          solution.candidate_captures &&
          setCandidateCaptures(prevProps => {
            const currentCaptures = [
              ...prevProps,
              ...solution.candidate_captures,
            ];
            if (currentCaptures.length > 4) return currentCaptures.slice(0, 4);
            return currentCaptures;
          }),
      );
    }
  }, [interview]);

  return (
    interview && (
      <Container maxWidth={settings.themeStretch ? false : 'lg'}>
        <Stack
          spacing={1.5}
          direction="row"
          alignItems="center"
          sx={{
            mb: { xs: 3, md: 5 },
          }}
        >
          <Box sx={{ flexGrow: 1 }}>
            <Box sx={{ mb: '1rem' }}>
              <Typography variant="h4">{interview.name}</Typography>
            </Box>
            <Box>
              <Button
                onClick={handleBackButton}
                startIcon={
                  <Iconify icon="eva:arrow-ios-back-fill" width={16} />
                }
              >
                Back
              </Button>
            </Box>
          </Box>
        </Stack>
        <InterviewResult score={score} interview={interview} />
        <CandidateCaptures candidateCaptures={candidateCaptures} />
        <ScreenRecordings screenRecordings={interview.screen_recordings} />
      </Container>
    )
  );
};

InterviewResultDashboard.propTypes = {
  interviewId: PropTypes.string,
  jobId: PropTypes.string,
  setTitle: PropTypes.func,
};

export default InterviewResultDashboard;
